import { useEffect, useState } from "react";
import { Box, Step, Stepper, useMediaQuery, Stack, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CustomStepLabel from "./components/Stepper/CustomStepLabel";
import CustomStepConnector from "./components/Stepper/CustomStepConnector";
import CustomStepIcon from "./components/Stepper/CustomStepIcon";
import MobileStepper from "./components/MobileStepper/MobileStepper";
import useFragments from "../../hooks/useFragments";
import styles from "../../styles/Subscribe.module.scss";
import SubscribeThematics from "./thematics";
import SubscribeCart from "./cart";
import SubscribeInformations from "./informations";
import PaymentForm from "./payment";
import OrderConfirmation from "./orderconfirmation";
import { STRIPE_INFO } from "./constants";

const StepComponentMap = (step, nextStep, previousStep, lastStep) => ({
  0: <SubscribeThematics nextStep={nextStep} />,
  1: <SubscribeCart nextStep={nextStep} previousStep={previousStep} />,
  2: <SubscribeInformations nextStep={nextStep} lastStep={lastStep} />,
  3: <PaymentForm nextStep={nextStep} />,
  4: <OrderConfirmation />
}[step]);

const SubscriptionSelection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { urlFragments, removeFragments } = useFragments();

  const [activeStep, setActiveStep] = useState(0);
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [pendingStepIndex, setPendingStepIndex] = useState(null);

  const nextStep = () => {
    setActiveStep(activeStep + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const previousStep = () => {
    setActiveStep(activeStep - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const lastStep = () => {
    setActiveStep(steps.length);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const targetedStep = (stepIndex) => {
    if (stepIndex > activeStep) {
      return;
    }
    if (activeStep === 3 && stepIndex < activeStep) {
      setOpenWarningModal(true);
      setPendingStepIndex(stepIndex);
      return;
    }
    if (stepIndex === 3 && activeStep < 3) {
      return;
    }
    setActiveStep(stepIndex);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleWarningConfirm = () => {
    setOpenWarningModal(false);
    if (pendingStepIndex !== null) {
      // Removing the stripe info from the local storage if user decide to go back to the payment step  
      localStorage.removeItem(STRIPE_INFO);
      setActiveStep(pendingStepIndex);
      setPendingStepIndex(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleWarningCancel = () => {
    setOpenWarningModal(false);
    setPendingStepIndex(null);
  };

  const steps = ["Thématiques", "Panier", "Informations", "Paiement"];

  // Set the active step from the URL fragment. Used by cart button.
  useEffect(() => {
    if (urlFragments.step) {
      setActiveStep(parseInt(urlFragments.step));
      removeFragments(["step"]);
    }
  }, [urlFragments]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem(STRIPE_INFO);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      localStorage.removeItem(STRIPE_INFO);
    };
  }, [])

  return (
    <Stack direction="column">
      {activeStep !== 4 && (
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#155A79",
            paddingTop: 4,
            paddingBottom: 4,
            paddingLeft: 1,
            paddingRight: 1
          }}
        >
          {isMobile ? (
            <MobileStepper
              steps={steps}
              activeStep={activeStep}
              onStepClick={(index) => targetedStep(index)}
            />
          ) : (
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              connector={<CustomStepConnector />}
            >
              {steps.map((label, index) => (
                <Step key={label}>
                  <CustomStepLabel
                    StepIconComponent={CustomStepIcon}
                    onClick={() => targetedStep(index)}
                  >
                    {label}
                  </CustomStepLabel>
                </Step>
              ))}
            </Stepper>
          )}
        </Box>
      )}
      <Dialog open={openWarningModal} onClose={handleWarningCancel}>
        <DialogTitle>Attention</DialogTitle>
        <DialogContent>
          En revenant en arri&egrave;re, vos informations seront r&eacute;initialis&eacute;es.<br />
          &Ecirc;tes-vous s&ucirc;r de vouloir continuer ?
        </DialogContent>
        <DialogActions>
          <button className={styles.buttonDialog} onClick={handleWarningCancel}>Annuler</button>
          <button className={styles.buttonDialog} onClick={handleWarningConfirm}>Continuer</button>
        </DialogActions>
      </Dialog>
      {StepComponentMap(activeStep, nextStep, previousStep, lastStep)}
    </Stack>
  );
};

export default SubscriptionSelection;
